import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { alpha, useTheme } from "@mui/material/styles";
import { Box, IconButton, TableContainer } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const ScrollableTableContainer = forwardRef(function ScrollableTableContainer(
  { children, sx, scrollStep = 280, showScrollButtons = true, ...props },
  forwardedRef
) {
  const theme = useTheme();
  const containerRef = useRef(null);

  useImperativeHandle(forwardedRef, () => containerRef.current);

  const scrollHorizontally = (direction) => {
    if (!containerRef.current) return;
    containerRef.current.scrollBy({
      left: direction * scrollStep,
      behavior: "smooth",
    });
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      scrollHorizontally(1);
    }

    if (event.key === "ArrowLeft") {
      event.preventDefault();
      scrollHorizontally(-1);
    }
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      {showScrollButtons ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 0.5,
            mb: 1,
          }}
        >
          <IconButton
            size="small"
            onClick={() => scrollHorizontally(-1)}
            sx={{
              bgcolor: alpha(theme.palette.background.paper, 0.92),
              border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
              boxShadow: theme.shadows[1],
              "&:hover": { bgcolor: theme.palette.background.paper },
            }}
            aria-label="Scroll table left"
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => scrollHorizontally(1)}
            sx={{
              bgcolor: alpha(theme.palette.background.paper, 0.92),
              border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
              boxShadow: theme.shadows[1],
              "&:hover": { bgcolor: theme.palette.background.paper },
            }}
            aria-label="Scroll table right"
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : null}

      <TableContainer
        ref={containerRef}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        sx={{
          overflowX: "auto",
          scrollBehavior: "smooth",
          "&:focus-visible": {
            outline: `2px solid ${alpha(theme.palette.primary.main, 0.8)}`,
            outlineOffset: 2,
          },
          ...sx,
        }}
        {...props}
      >
        {children}
      </TableContainer>
    </Box>
  );
});

export default ScrollableTableContainer;
